import React, { useState, useEffect } from 'react';
import {
    Plus,
    Edit,
    Trash2,
    Eye,
    EyeOff,
    Archive,
    ArchiveRestore,
    ExternalLink,
    Calendar,
    Tag,
    User,
    Filter,
    Search,
    X
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import {
    getAllPortfolio,
    createPortfolio,
    updatePortfolio,
    deletePortfolio,
    archivePortfolio,
    activatePortfolio,
    togglePortfolioPublic,
    PORTFOLIO_CATEGORIES,
    PORTFOLIO_STATUS
} from '../services/portfolioService';
import { AccessDeniedCard } from './AccessDeniedCard';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

export function PortfolioManagement() {
    const [portfolio, setPortfolio] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [editingPortfolio, setEditingPortfolio] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        projectUrl: '',
        imageUrl: '',
        technologies: [],
        category: 'web_development',
        duration: '',
        budget: '',
        client: '',
        projectDate: '',
        completedAt: '',
        isPublic: true
    });

    const {
        canCreatePortfolio,
        canReadPortfolio,
        canUpdatePortfolio,
        canDeletePortfolio
    } = usePermissions();

    useEffect(() => {
        loadPortfolio();
    }, [searchTerm, statusFilter, categoryFilter]);

    const loadPortfolio = async () => {
        try {
            setLoading(true);
            const params = {
                page: 1,
                limit: 50
            };

            if (statusFilter) params.status = statusFilter;
            if (categoryFilter) params.category = categoryFilter;

            const response = await getAllPortfolio(params);
            if (response.success) {
                let filteredData = response.data;

                if (searchTerm) {
                    filteredData = filteredData.filter(item =>
                        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.client?.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                }

                setPortfolio(filteredData);
            }
        } catch (error) {
            setError('Failed to load portfolio projects');
            console.error('Error loading portfolio:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingPortfolio(null);
        setFormData({
            title: '',
            description: '',
            projectUrl: '',
            imageUrl: '',
            technologies: [],
            category: 'web_development',
            duration: '',
            budget: '',
            client: '',
            projectDate: '',
            completedAt: '',
            isPublic: true
        });
        setShowModal(true);
    };

    const handleEdit = (portfolio) => {
        setEditingPortfolio(portfolio);
        setFormData({
            title: portfolio.title,
            description: portfolio.description,
            projectUrl: portfolio.projectUrl,
            imageUrl: portfolio.imageUrl || '',
            technologies: portfolio.technologies || [],
            category: portfolio.category,
            duration: portfolio.duration || '',
            budget: portfolio.budget || '',
            client: portfolio.client || '',
            projectDate: portfolio.projectDate ? new Date(portfolio.projectDate).toISOString().split('T')[0] : '',
            completedAt: portfolio.completedAt ? new Date(portfolio.completedAt).toISOString().split('T')[0] : '',
            isPublic: portfolio.isPublic
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            setLoading(true);

            const portfolioData = {
                ...formData,
                technologies: formData.technologies.filter(tech => tech.trim() !== '')
            };

            if (editingPortfolio) {
                await updatePortfolio(editingPortfolio._id, portfolioData);
            } else {
                await createPortfolio(portfolioData);
            }

            setShowModal(false);
            loadPortfolio();
        } catch (error) {
            setError(`Failed to ${editingPortfolio ? 'update' : 'create'} portfolio project`);
            console.error('Error saving portfolio:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (portfolioId) => {
        if (!window.confirm('Are you sure you want to delete this portfolio project?')) {
            return;
        }

        try {
            setLoading(true);
            await deletePortfolio(portfolioId);
            loadPortfolio();
        } catch (error) {
            setError(`Failed to delete portfolio project: ${error.message}`);
            console.error('Error deleting portfolio:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async (portfolioId) => {
        try {
            setLoading(true);
            await archivePortfolio(portfolioId);
            loadPortfolio();
        } catch (error) {
            setError('Failed to archive portfolio project');
            console.error('Error archiving portfolio:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async (portfolioId) => {
        try {
            setLoading(true);
            await activatePortfolio(portfolioId);
            loadPortfolio();
        } catch (error) {
            setError('Failed to activate portfolio project');
            console.error('Error activating portfolio:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePublic = async (portfolioId) => {
        try {
            setLoading(true);
            await togglePortfolioPublic(portfolioId);
            loadPortfolio();
        } catch (error) {
            setError('Failed to toggle portfolio public status');
            console.error('Error toggling portfolio public status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProjectClick = (project) => {
        setSelectedProject(project);
        setShowDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedProject(null);
    };

    const addTechnology = () => {
        setFormData(prev => ({
            ...prev,
            technologies: [...prev.technologies, '']
        }));
    };

    const updateTechnology = (index, value) => {
        setFormData(prev => ({
            ...prev,
            technologies: prev.technologies.map((tech, i) => i === index ? value : tech)
        }));
    };

    const removeTechnology = (index) => {
        setFormData(prev => ({
            ...prev,
            technologies: prev.technologies.filter((_, i) => i !== index)
        }));
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { color: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20', label: 'Active' },
            archived: { color: 'bg-gray-100 text-gray-800', label: 'Archived' },
            draft: { color: 'bg-yellow-100 text-yellow-800', label: 'Draft' }
        };
        const config = statusConfig[status] || statusConfig.active;
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!canReadPortfolio()) {
        return <AccessDeniedCard message="You don't have permission to view portfolio projects." />;
    }

    return (
        <AdminPageLayout>
            <AdminCard bodyClassName="p-0" className="overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 space-y-3">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Filters */}
                    <div className="admin-filter-panel">
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="flex-1 min-w-[240px] max-w-md">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                    <input
                                        type="text"
                                        placeholder="Search projects..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="admin-filter-control pl-8 pr-3 bg-white placeholder:text-gray-400"
                                    />
                                </div>
                            </div>

                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="admin-filter-control w-auto min-w-[160px]"
                            >
                                <option value="">All Status</option>
                                {Object.entries(PORTFOLIO_STATUS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>

                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="admin-filter-control w-auto min-w-[180px]"
                            >
                                <option value="">All Categories</option>
                                {Object.entries(PORTFOLIO_CATEGORIES).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>

                            {canCreatePortfolio() && (
                                <button
                                    onClick={handleCreate}
                                    className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    Add Project
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Portfolio Table */}
                <div className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/50">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Project
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Category
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Visibility
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                                            <span className="text-sm text-gray-500">Loading projects...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : portfolio.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                </svg>
                                            </div>
                                            <h3 className="text-sm font-medium text-gray-900 mb-1">No portfolio projects yet</h3>
                                            <p className="text-sm text-gray-500 mb-4 max-w-xs">Showcase your best work by adding portfolio projects.</p>
                                            {canCreatePortfolio() && (
                                                <button
                                                    onClick={handleCreate}
                                                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                                                >
                                                    Add Your First Project
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                portfolio.map((item) => (
                                    <tr key={item._id} className="group hover:bg-gray-50/50 cursor-pointer transition-colors" onClick={(e) => {
                                        // Don't open details modal if clicking on action buttons
                                        if (e.target.closest('button') || e.target.closest('a')) {
                                            return;
                                        }
                                        handleProjectClick(item);
                                    }}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center">
                                                {item.imageUrl && (
                                                    <img
                                                        src={item.imageUrl}
                                                        alt={item.title}
                                                        className="h-9 w-9 rounded-md object-cover mr-3"
                                                    />
                                                )}
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                                                    <div className="text-xs text-gray-500 truncate max-w-xs">
                                                        {item.description?.slice(0, 40)}...
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {PORTFOLIO_CATEGORIES[item.category] || item.category}
                                        </td>
                                        <td className="px-4 py-3">
                                            {getStatusBadge(item.status)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                {item.isPublic ? (
                                                    <Eye className="h-3.5 w-3.5 text-success-600" />
                                                ) : (
                                                    <EyeOff className="h-3.5 w-3.5 text-gray-400" />
                                                )}
                                                <span className="text-sm text-gray-700">
                                                    {item.isPublic ? 'Public' : 'Private'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {formatDate(item.projectDate)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                                                <a
                                                    href={item.projectUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                                                    title="Open project URL"
                                                >
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                </a>

                                                {canUpdatePortfolio() && (
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                                                        title="Edit project"
                                                    >
                                                        <Edit className="h-3.5 w-3.5" />
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => handleTogglePublic(item._id)}
                                                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                                                    title={item.isPublic ? 'Make private' : 'Make public'}
                                                >
                                                    {item.isPublic ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                                </button>

                                                {item.status === 'active' ? (
                                                    <button
                                                        onClick={() => handleArchive(item._id)}
                                                        className="p-1.5 text-gray-500 hover:text-accent-600 hover:bg-accent-50 rounded-md transition-colors"
                                                        title="Archive project"
                                                    >
                                                        <Archive className="h-3.5 w-3.5" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleActivate(item._id)}
                                                        className="p-1.5 text-gray-500 hover:text-success-600 hover:bg-success-50 rounded-md transition-colors"
                                                        title="Activate project"
                                                    >
                                                        <ArchiveRestore className="h-3.5 w-3.5" />
                                                    </button>
                                                )}

                                                {canDeletePortfolio() && (
                                                    <button
                                                        onClick={() => handleDelete(item._id)}
                                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                        title="Delete project (cannot be undone)"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        </table>
                    </div>
                </div>
            </AdminCard>

            {/* Portfolio Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="relative p-6 bg-white w-full max-w-2xl mx-auto rounded-lg shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
                        <button
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowModal(false)}
                            type="button"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <h3 className="text-lg font-semibold mb-4">
                            {editingPortfolio ? 'Edit Portfolio Project' : 'Add Portfolio Project'}
                        </h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Category *
                                    </label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                        required
                                    >
                                        {Object.entries(PORTFOLIO_CATEGORIES).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Description *
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    rows={3}
                                    className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Project URL *
                                    </label>
                                    <input
                                        type="url"
                                        value={formData.projectUrl}
                                        onChange={(e) => setFormData(prev => ({ ...prev, projectUrl: e.target.value }))}
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Image URL
                                    </label>
                                    <input
                                        type="url"
                                        value={formData.imageUrl}
                                        onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Technologies
                                </label>
                                <div className="space-y-2">
                                    {formData.technologies.map((tech, index) => (
                                        <div key={index} className="flex gap-2">
                                            <input
                                                type="text"
                                                value={tech}
                                                onChange={(e) => updateTechnology(index, e.target.value)}
                                                placeholder="Enter technology"
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeTechnology(index)}
                                                className="px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addTechnology}
                                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                                    >
                                        + Add Technology
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Duration
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.duration}
                                        onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                                        placeholder="e.g., 3 months"
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Budget
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.budget}
                                        onChange={(e) => setFormData(prev => ({ ...prev, budget: e.target.value }))}
                                        placeholder="e.g., $5,000"
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Client
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.client}
                                        onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                                        placeholder="Client name"
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Project Date *
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.projectDate}
                                        onChange={(e) => setFormData(prev => ({ ...prev, projectDate: e.target.value }))}
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Completed Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.completedAt}
                                        onChange={(e) => setFormData(prev => ({ ...prev, completedAt: e.target.value }))}
                                        className="block w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="isPublic"
                                    checked={formData.isPublic}
                                    onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                                    className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-900">
                                    Make this project public
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-5 pt-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Saving...' : (editingPortfolio ? 'Update' : 'Create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Project Details Modal */}
            {showDetailsModal && selectedProject && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="relative p-6 bg-white w-full max-w-2xl mx-auto rounded-lg shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
                        <button
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                            onClick={closeDetailsModal}
                            type="button"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <h3 className="text-lg font-semibold mb-4">{selectedProject.title}</h3>

                        <div className="space-y-4">
                            {selectedProject.imageUrl && (
                                <div className="mb-4">
                                    <img
                                        src={selectedProject.imageUrl}
                                        alt={selectedProject.title}
                                        className="w-full h-48 object-cover rounded-lg"
                                    />
                                </div>
                            )}

                            <div>
                                <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                                <p className="text-gray-700">{selectedProject.description}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-1">Category</h4>
                                    <p className="text-gray-700">{PORTFOLIO_CATEGORIES[selectedProject.category] || selectedProject.category}</p>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-1">Status</h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedProject.status === 'active' ? 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20' :
                                        selectedProject.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {PORTFOLIO_STATUS[selectedProject.status] || selectedProject.status}
                                    </span>
                                </div>
                            </div>

                            {selectedProject.technologies && selectedProject.technologies.length > 0 && (
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Technologies</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedProject.technologies.map((tech, index) => (
                                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                {selectedProject.duration && (
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Duration</h4>
                                        <p className="text-gray-700">{selectedProject.duration}</p>
                                    </div>
                                )}
                                {selectedProject.budget && (
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Budget</h4>
                                        <p className="text-gray-700">{selectedProject.budget}</p>
                                    </div>
                                )}
                            </div>

                            {selectedProject.client && (
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-1">Client</h4>
                                    <p className="text-gray-700">{selectedProject.client}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-1">Project Date</h4>
                                    <p className="text-gray-700">{formatDate(selectedProject.projectDate)}</p>
                                </div>
                                {selectedProject.completedAt && (
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">Completed Date</h4>
                                        <p className="text-gray-700">{formatDate(selectedProject.completedAt)}</p>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    {selectedProject.isPublic ? (
                                        <Eye className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    )}
                                    <span className="text-sm text-gray-700">
                                        {selectedProject.isPublic ? 'Public' : 'Private'}
                                    </span>
                                </div>
                            </div>

                            {selectedProject.projectUrl && (
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Project URL</h4>
                                    <a
                                        href={selectedProject.projectUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        View Project
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={closeDetailsModal}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminPageLayout>
    );
}
